import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { CTA, GlassCard, PageHeader, Shell } from "../components/site/Primitives";

const VOLUMES = ["Under 50", "50 – 200", "200 – 500", "500 – 2,000", "2,000+"];

export default function BookDemo() {
  const [industries, setIndustries] = useState([]);
  const [form, setForm] = useState({
    first_name: "", last_name: "", business_name: "", email: "", phone: "",
    industry: "", website: "", goals: "", monthly_volume: VOLUMES[1], preferred_time: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [calendarConnected] = useState(false);

  useEffect(() => {
    api
      .get("/public/industry-templates")
      .then(({ data }) => {
        setIndustries(data.map((d) => d.name));
        setForm((f) => ({ ...f, industry: f.industry || data[0]?.name || "Other" }));
      })
      .catch(() => setIndustries(["Dental", "HVAC", "Plumbing", "Law Firm", "Other"]));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const n = {};
    if (!form.first_name.trim()) n.first_name = "Required.";
    if (!form.last_name.trim()) n.last_name = "Required.";
    if (!form.business_name.trim()) n.business_name = "Required.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) n.email = "Enter a valid email address.";
    if (form.phone.trim().length < 5) n.phone = "Enter a contactable number.";
    if (form.goals.trim().length < 5) n.goals = "Tell us what you'd like to automate.";
    setErrors(n);
    return Object.keys(n).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please correct the highlighted fields.");
      return;
    }
    setStatus("loading");
    try {
      await api.post("/public/demo-request", form);
      setStatus("success");
      toast.success("Demo request received.");
    } catch (err) {
      setStatus("error");
      toast.error(apiError(err));
    }
  };

  if (status === "success")
    return (
      <div data-testid="book-demo-page">
        <PageHeader testId="book-demo-title" eyebrow="Book a demo" title="Request received." />
        <section className="py-20">
          <Shell>
            <GlassCard testId="book-demo-success" hover={false} className="max-w-2xl p-8">
              <Icons.CheckCircle2 className="h-6 w-6 text-elv-green" />
              <p className="mt-5 text-lg text-elv-text">
                Thanks {form.first_name} — your demo request is logged.
              </p>
              <p className="mt-3 text-sm text-elv-dim">
                We've created a lead record for {form.business_name} and notified the Elvoria team. You'll get an
                email to confirm a time that works.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <CTA to="/features" variant="ghost" testId="book-demo-success-features">
                  Explore the platform
                </CTA>
                <CTA to="/" variant="quiet" testId="book-demo-success-home">
                  Back to home
                </CTA>
              </div>
            </GlassCard>
          </Shell>
        </section>
      </div>
    );

  return (
    <div data-testid="book-demo-page">
      <PageHeader
        testId="book-demo-title"
        eyebrow="Book a demo"
        title="Book a Free AI Demo."
        sub="Thirty minutes, your workflow, the actual platform. We'll show what can be automated first and what it would cost to run."
      />

      <section className="py-20">
        <Shell>
          <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
            <GlassCard testId="book-demo-form-card" hover={false} className="p-6 sm:p-8">
              <form onSubmit={submit} noValidate className="grid gap-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Input id="first_name" label="First name" required value={form.first_name} onChange={set("first_name")} error={errors.first_name} />
                  <Input id="last_name" label="Last name" required value={form.last_name} onChange={set("last_name")} error={errors.last_name} />
                </div>
                <Input id="business_name" label="Business name" required value={form.business_name} onChange={set("business_name")} error={errors.business_name} />
                <div className="grid gap-5 sm:grid-cols-2">
                  <Input id="email" label="Email" type="email" required value={form.email} onChange={set("email")} error={errors.email} />
                  <Input id="phone" label="Phone" required value={form.phone} onChange={set("phone")} error={errors.phone} />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Select id="industry" label="Industry" value={form.industry} onChange={set("industry")} options={[...industries, "Other"]} />
                  <Input id="website" label="Website" placeholder="https://" value={form.website} onChange={set("website")} />
                </div>
                <div>
                  <label htmlFor="goals" className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">
                    What do you want to automate? *
                  </label>
                  <textarea
                    id="goals"
                    rows={4}
                    data-testid="demo-input-goals"
                    value={form.goals}
                    onChange={set("goals")}
                    className="mt-2.5 w-full rounded-md border border-elv-line bg-white/[0.02] px-3.5 py-3 text-sm text-elv-text outline-none focus:border-elv-green/50"
                  />
                  {errors.goals ? (
                    <p data-testid="demo-error-goals" className="mt-2 text-xs text-red-300">
                      {errors.goals}
                    </p>
                  ) : null}
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Select id="monthly_volume" label="Monthly customer volume" value={form.monthly_volume} onChange={set("monthly_volume")} options={VOLUMES} />
                  <Input id="preferred_time" label="Preferred demo date/time" type="datetime-local" value={form.preferred_time} onChange={set("preferred_time")} />
                </div>

                <div
                  data-testid="calendar-connection-notice"
                  className="rounded-md border border-amber-300/25 bg-amber-300/[0.05] p-4 text-xs leading-relaxed text-amber-200"
                >
                  <span className="font-medium">Google Calendar not connected.</span> Live slot booking is
                  disabled until a calendar provider is connected, so this form captures your preferred time as a
                  lead and we confirm by email.
                  {calendarConnected ? null : (
                    <span className="mt-2 block text-amber-200/80">Connection required.</span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    data-testid="demo-submit"
                    disabled={status === "loading"}
                    className="inline-flex items-center gap-2 rounded-md bg-elv-green px-6 py-3 text-sm font-semibold text-[#04120A] transition-colors duration-300 hover:bg-[#5cffa0] disabled:opacity-60"
                  >
                    {status === "loading" ? (
                      <Icons.Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Icons.CalendarCheck className="h-4 w-4" />
                    )}
                    {status === "loading" ? "Submitting…" : "Book a Free AI Demo"}
                  </button>
                  {status === "error" ? (
                    <button
                      type="submit"
                      data-testid="demo-retry"
                      className="text-xs text-elv-dim underline underline-offset-4 hover:text-elv-text"
                    >
                      Retry
                    </button>
                  ) : null}
                </div>
              </form>
            </GlassCard>

            <div className="grid gap-3">
              {[
                ["What we cover", ["Your current enquiry-to-booking flow", "Where automation fits first", "AI receptionist scope", "Realistic running costs"], "ListChecks"],
                ["What you get", ["A workflow map of your funnel", "A shortlist of automations", "A written follow-up summary"], "FileText"],
                ["What we won't do", ["Invent results", "Claim unconnected integrations are live", "Promise unlimited usage"], "ShieldCheck"],
              ].map(([title, items, icon], i) => {
                const Icon = Icons[icon];
                return (
                  <GlassCard key={title} testId={`demo-aside-${i}`} className="p-6">
                    <Icon className="h-4 w-4 text-elv-cyan" />
                    <p className="mt-4 text-sm font-semibold text-elv-text">{title}</p>
                    <ul className="mt-3 space-y-2">
                      {items.map((it) => (
                        <li key={it} className="text-xs text-elv-dim">
                          — {it}
                        </li>
                      ))}
                    </ul>
                  </GlassCard>
                );
              })}
            </div>
          </div>
        </Shell>
      </section>
    </div>
  );
}

const Input = ({ id, label, type = "text", required, value, onChange, error, placeholder }) => (
  <div>
    <label htmlFor={id} className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">
      {label} {required ? "*" : ""}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      data-testid={`demo-input-${id}`}
      className="mt-2.5 w-full rounded-md border border-elv-line bg-white/[0.02] px-3.5 py-3 text-sm text-elv-text outline-none focus:border-elv-green/50"
    />
    {error ? (
      <p data-testid={`demo-error-${id}`} className="mt-2 text-xs text-red-300">
        {error}
      </p>
    ) : null}
  </div>
);

const Select = ({ id, label, value, onChange, options }) => (
  <div>
    <label htmlFor={id} className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">
      {label}
    </label>
    <select
      id={id}
      value={value}
      onChange={onChange}
      data-testid={`demo-input-${id}`}
      className="mt-2.5 w-full rounded-md border border-elv-line bg-[#0A0F14] px-3.5 py-3 text-sm text-elv-text outline-none focus:border-elv-green/50"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  </div>
);
